import { useMemo } from 'react';
import { useLeaksQuery } from './queries/useLeaksQuery';
import { useRansomwareQuery } from './queries/useRansomwareQuery';
import { useCVEQuery } from './queries/useCVEQuery';
import { useNewsQuery } from './queries/useNewsQuery';
import { useEOLQuery } from './queries/useEOLQuery';
import { useReleasesQuery } from './queries/useReleasesQuery';

import { defaultDashboardData } from "@/types/dashboard";
import { useLanguage } from "./useLanguage";

import { calculateTrend } from '@/utils/metricCalculations';
import { processNewsData, extractNewsItems } from '@/utils/newsDataProcessor';
import { processLeaksData, countRecentLeaks } from '@/utils/leakDataProcessor';
import { processCVEData } from '@/utils/vulnerabilityProcessor';
import { processRansomwareData } from '@/utils/ransomwareProcessor';
import { processEOLData, countUpcomingEOLItems } from '@/utils/eolDataProcessors';
import { findMostAttackedCountry } from '@/utils/countryDataProcessors';

export function useDashboardData(days: number = 30) {
  const { language } = useLanguage();

  const leaksQuery = useLeaksQuery();
  const ransomwareQuery = useRansomwareQuery();
  const cveQuery = useCVEQuery();
  const newsQuery = useNewsQuery();
  const eolQuery = useEOLQuery();
  const releasesQuery = useReleasesQuery();

  const isLoading =
    leaksQuery.isLoading ||
    ransomwareQuery.isLoading ||
    cveQuery.isLoading ||
    newsQuery.isLoading ||
    eolQuery.isLoading ||
    releasesQuery.isLoading;

  const isError =
    leaksQuery.isError ||
    ransomwareQuery.isError ||
    cveQuery.isError ||
    newsQuery.isError ||
    eolQuery.isError ||
    releasesQuery.isError;

  const error = leaksQuery.error || ransomwareQuery.error || cveQuery.error; // Just catch first error

  const data = useMemo(() => {
    if (isLoading) return defaultDashboardData;

    try {
      const leaksData = leaksQuery.data || [];
      const ransomwareData = ransomwareQuery.data || [];
      const cveData = cveQuery.data || [];
      const [newsEnData, newsPlData] = newsQuery.data || [[], []];
      const eolData = eolQuery.data || {};
      const releasesData = releasesQuery.data || [];

      // Merge news data
      const mergedNews = {
        items: [
          ...(Array.isArray(newsEnData) ? newsEnData : (newsEnData?.items || newsEnData?.news || [])).map((item: any) => ({ ...item, lang: 'en' })),
          ...(Array.isArray(newsPlData) ? newsPlData : (newsPlData?.items || newsPlData?.news || [])).map((item: any) => ({ ...item, lang: 'pl' }))
        ]
      };

      const recentLeaks = processLeaksData(leaksData);
      const leaksCounts = countRecentLeaks(leaksData, days);
      const { recentRansomwareAttacks, allRansomwareAttacks, mostActiveGroup, victimCount, previousVictimCount } = processRansomwareData(ransomwareData, days);
      const {
        criticalVulnerabilities,
        highVulnerabilities,
        mediumVulnerabilities,
        lowVulnerabilities,
        recentCriticalVulnerabilities,
        recentHighVulnerabilities,
        recentMediumVulnerabilities,
        recentLowVulnerabilities,
        prevCriticalCount,
        prevHighCount,
        prevMediumCount,
        prevLowCount
      } = processCVEData(cveData, days);


      const latestNews = processNewsData(mergedNews);
      const allNewsItems = extractNewsItems(mergedNews);
      const upcomingEOL = processEOLData(eolData || {});
      const eolItemsCounts = countUpcomingEOLItems(eolData || {}, days);
      const mostAttackedCountry = findMostAttackedCountry(ransomwareData || []);

      const normalizedReleases = Array.isArray(releasesData)
        ? releasesData
        : (releasesData?.releases && Array.isArray(releasesData.releases) ? releasesData.releases : []);

      const allReleasesData = normalizedReleases
        .sort((a: any, b: any) => new Date(b.data_wydania).getTime() - new Date(a.data_wydania).getTime());

      const latestReleases = allReleasesData;

      return {
        eolItemsCount: calculateTrend('eolItemsCount', eolItemsCounts.current, eolItemsCounts.previous),
        leaksCount: calculateTrend('leaksCount', leaksCounts.current, leaksCounts.previous),
        mostActiveRansomwareGroup: mostActiveGroup,
        mostAttackedCountry,
        ransomwareVictimsCount: calculateTrend('ransomwareVictimsCount', victimCount, previousVictimCount),
        criticalVulnerabilitiesCount: calculateTrend('criticalVulnerabilities', criticalVulnerabilities.length, prevCriticalCount),
        highVulnerabilitiesCount: calculateTrend('highVulnerabilities', highVulnerabilities.length, prevHighCount),
        mediumVulnerabilitiesCount: calculateTrend('mediumVulnerabilities', mediumVulnerabilities.length, prevMediumCount),
        lowVulnerabilitiesCount: calculateTrend('lowVulnerabilities', lowVulnerabilities.length, prevLowCount),
        latestNews,
        allNews: allNewsItems,
        recentLeaks,
        recentCriticalVulnerabilities,
        recentHighVulnerabilities,
        recentMediumVulnerabilities,
        recentLowVulnerabilities,
        upcomingEOL,
        latestReleases,
        allReleases: allReleasesData,
        recentRansomwareAttacks,
        allRansomwareAttacks
      };
    } catch (e) {
      console.error("Error processing dashboard data", e);
      return defaultDashboardData;
    }
  }, [
    leaksQuery.data,
    ransomwareQuery.data,
    cveQuery.data,
    newsQuery.data,
    eolQuery.data,
    releasesQuery.data,
    days,
    isLoading
  ]);

  const refetch = () => {
    leaksQuery.refetch();
    ransomwareQuery.refetch();
    cveQuery.refetch();
    newsQuery.refetch();
    eolQuery.refetch();
    releasesQuery.refetch();
  };

  return {
    data,
    isLoading,
    error,
    isError,
    refetch,
    isRefetching: leaksQuery.isRefetching // Approximated
  };
}
